import { motion } from "framer-motion";
import SplitReveal from "@/components/anim/SplitReveal";

const MEMBERS = [
  {
    id: "andrej",
    name: "Andrej Oblender",
    firstName: "Andrej",
    lastName: "Oblender",
    role: "Creative Director",
    focus: "Ansprechpartner Medienerstellung",
    skills: [
      "Marketingstrategie",
      "Mediale Umsetzung",
      "Konzeption",
      "Videoproduktion",
      "Fotografie",
    ],
    image: "/media/team/andrej.webp",
  },
  {
    id: "bjoern",
    name: "Björn Hertlein",
    firstName: "Björn",
    lastName: "Hertlein",
    role: "Vertrieb & Digital",
    focus: "Kundenkontakt & Onlinepräsenz",
    skills: [
      "SEO Optimierung",
      "Website Building",
      "Google Ads",
      "Performance Marketing",
      "Kundenberatung",
    ],
    image: "/media/team/bjoern.webp",
  },
  {
    id: "ayleen",
    name: "Ayleen K.",
    firstName: "Ayleen",
    lastName: "K.",
    role: "Foto & Videografin",
    focus: "Bild- & Bewegtbildproduktion",
    skills: [
      "Bewegtbild",
      "Fotografie",
      "Editing",
      "Contentproduktion",
      "Setregie",
    ],
    image: "/media/team/ayleen.webp",
  },
];

export default function Team() {
  return (
    <section
      id="team"
      data-testid="team-section"
      className="relative bg-ink py-24 lg:py-40 px-6 lg:px-12 overflow-hidden"
    >
      {/* Ambient cyan glow to tie the section to the CRE8 accent palette */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(63,211,219,0.10) 0%, rgba(63,211,219,0.03) 40%, rgba(63,211,219,0) 70%)",
          filter: "blur(30px)",
        }}
      />

      <div className="relative mx-auto max-w-[1400px]">
        {/* Section header */}
        <div className="mb-14 lg:mb-20">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-bone/60 mb-6"
          >
            <span className="h-px w-10 bg-signal" />
            Team
          </motion.div>
          <SplitReveal
            as="h2"
            lines={["Das sind wir."]}
            className="font-display font-black tracking-tighter text-bone leading-[0.9] whitespace-nowrap"
            duration={1.0}
            stagger={0.05}
          />
          <style>{`#team h2 { font-size: clamp(1.5rem, 3.2vw, 2.75rem); }`}</style>
        </div>

        {/* Members, horizontal swipe carousel on mobile, 2-col grid on desktop */}
        <div
          className="md:hidden -mx-6 px-6 flex snap-x snap-mandatory overflow-x-auto gap-10 pb-6 scroll-smooth [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
          style={{ WebkitOverflowScrolling: "touch" }}
          data-testid="team-swiper"
        >
          {MEMBERS.map((m, i) => (
            <div
              key={m.id}
              className="snap-center shrink-0 w-[72%] max-w-[300px] first:ml-4 last:mr-4"
            >
              <MemberCard member={m} delay={i * 0.15} />
            </div>
          ))}
        </div>

        <div className="hidden md:grid md:grid-cols-3 gap-10 lg:gap-14">
          {MEMBERS.map((m, i) => (
            <MemberCard key={m.id} member={m} delay={i * 0.15} />
          ))}
        </div>

        {/* Swipe hint dots, mobile only */}
        <div
          className="md:hidden mt-4 flex items-center justify-center gap-2"
          aria-hidden
        >
          {MEMBERS.map((m) => (
            <span key={m.id} className="h-1 w-6 rounded-full bg-white/15" />
          ))}
        </div>
      </div>
    </section>
  );
}

function MemberCard({ member, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay }}
      className="flex flex-col items-center text-center"
      data-testid={`team-member-${member.id}`}
    >
      {/* Portrait with soft radial outer fade, a touch smaller than before
          to keep the swipe carousel feeling airy on mobile. */}
      <div className="relative w-full max-w-[360px] aspect-[4/5]">
        <img
          src={member.image}
          alt={`${member.name}. ${member.role} bei CRE8 Studio GmbH`}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover object-center"
          style={{
            WebkitMaskImage:
              "radial-gradient(ellipse 62% 72% at 50% 42%, black 25%, rgba(0,0,0,0.85) 50%, rgba(0,0,0,0.4) 78%, rgba(0,0,0,0) 100%)",
            maskImage:
              "radial-gradient(ellipse 62% 72% at 50% 42%, black 25%, rgba(0,0,0,0.85) 50%, rgba(0,0,0,0.4) 78%, rgba(0,0,0,0) 100%)",
          }}
        />
      </div>

      <div className="mt-6 lg:mt-8 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-bone/50">
        <span className="h-px w-8 bg-signal" />
        Das ist
      </div>
      <h3
        className="mt-3 font-display font-black tracking-tighter text-bone leading-[0.95]"
        style={{ fontSize: "clamp(1.6rem, 3vw, 2.5rem)" }}
      >
        {member.firstName} <span className="text-signal">{member.lastName}</span>
      </h3>
      <p className="mt-4 text-base text-bone/70 leading-relaxed max-w-[320px]">
        <span className="text-bone">{member.role}</span>
        <span className="text-bone/40 mx-2">·</span>
        {member.focus}
      </p>

      {/* Skill-Tags, zeigen konkrete Zuständigkeiten pro Teammitglied */}
      {member.skills && member.skills.length > 0 && (
        <div
          data-testid={`team-${member.id}-skills`}
          className="mt-5 flex flex-wrap gap-2 max-w-[340px]"
        >
          {member.skills.map((skill) => (
            <span
              key={skill}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-bone/75 border border-white/15 rounded-full px-3 py-1.5 hover:border-signal/70 hover:text-signal transition-colors duration-300"
            >
              <span className="inline-block w-1 h-1 rounded-full bg-signal" />
              {skill}
            </span>
          ))}
        </div>
      )}
    </motion.div>
  );
}
